import { NOT_ASSIGNED } from 'constants/constants';
import React from 'react';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import usePcrV2 from '../../hooks/usePcrV2';
import VersionHistorySummaryGrid, { SummaryGridRow } from '../VersionHistorySummaryGrid';
import { EpdVersionHistoryData } from '../utils/EpdVersionHistoryDataContext';

const VersionHistorySystemBoundaryGrid: React.FC<{ data: EpdVersionHistoryData }> = ({ data }) => {
  const isPcrV2 = usePcrV2();
  const rows = getSystemBoundaryGridRows(data, isPcrV2);
  return <VersionHistorySummaryGrid rows={rows} />;
};

export default VersionHistorySystemBoundaryGrid;

export function getSystemBoundaryGridRows(data?: EpdVersionHistoryData, isPcrV2?: boolean) {
  const possibleRows: (SummaryGridRow | false)[] = [
    {
      ord: 1,
      name: 'Description of the system boundary',
      value: (() => {
        if (!data?.systemBoundary?.description) {
          return NOT_ASSIGNED;
        }
        const dictionaryCode = isPcrV2
          ? EPD_DICTIONARY_CODES.SYSTEM_BOUNDARY_DESCRIPTION_V2
          : EPD_DICTIONARY_CODES.SYSTEM_BOUNDARY_DESCRIPTION;
        const description = data.dictionaries.find(
          (d) => d.dictionaryCode === dictionaryCode && d.key === data?.systemBoundary?.description
        );
        if (!description) {
          return NOT_ASSIGNED;
        }
        return description.value;
      })(),
    },
    {
      ord: 2,
      name: 'Excluded modules',
      value: (() => {
        if (typeof data?.systemBoundary?.hasExcludedModules !== 'boolean') {
          return NOT_ASSIGNED;
        }
        return data?.systemBoundary.hasExcludedModules
          ? 'Yes, there is an excluded module, or there are excluded modules'
          : 'No, there is no excluded module, or there are no excluded modules';
      })(),
    },
    !!data?.systemBoundary?.hasExcludedModules && {
      ord: 3,
      name: 'Justification for the omission of modules',
      value: data?.systemBoundary.excludedModulesJustification ?? NOT_ASSIGNED,
    },
    !!data?.systemBoundary?.processFlowDiagramsDescription && {
      ord: 4,
      name: 'Description of the process flow diagram(s)',
      value: data?.systemBoundary.processFlowDiagramsDescription,
    },
  ];

  const rows = possibleRows.filter((r) => r) as SummaryGridRow[];

  return rows;
}
